import  { useState, useEffect } from 'react';
import {
  useResetDefaultPasswordMutation
} from '../../../features/authentication/authApiSlice';
import { useLocation, useNavigate } from 'react-router';
import { Button, Col, Form, ListGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faCircleCheck, faCircleXmark, faEnvelope, faCircleQuestion, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import successImage from '../../../assets/images/success.png';
import { faEnvelopeDot } from '@fortawesome/pro-solid-svg-icons';

function ResetDefaultPassword() {
  const [resetDefaultPassword, { isLoading }] = useResetDefaultPasswordMutation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordLengthValid, setPasswordLengthValid] = useState(false);
  const [passwordCharactersValid, setPasswordCharactersValid] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location?.state?.primaryEmail) {
      setPrimaryEmail(location.state.primaryEmail);
    }
  }, [location?.state?.primaryEmail]);

  useEffect(() => {
    if (location?.state?.secondaryEmail) {
      setSecondaryEmail(location.state.secondaryEmail);
    }
  }, [location?.state?.secondaryEmail]);

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    setPasswordValid(password.length >= 0 && location?.state?.login == password ? false : true);
    setPasswordLengthValid(password.length < 4 ? false : true);
    setPasswordCharactersValid(
      password.match('[",\'</]') || password.length == 0 ? false : true
    );
    setPasswordsMatch(
      confirmPassword && confirmPassword.length > 0 && confirmPassword != password
        ? false
        : true
    );
    setError('');
  };

  const handleConfirmPasswordBlur = (e) => {
    setPasswordsMatch(
      newPassword && newPassword.length > 0 && newPassword != e.target.value ? false : true
    );
    setError('');
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!passwordValid || !passwordLengthValid || !passwordCharactersValid || !passwordsMatch)
      return;

    try {
      await resetDefaultPassword({
        newPassword: newPassword,
        email : primaryEmail,
        secondaryEmail: secondaryEmail
      }).unwrap();
 
      setNewPassword('');
      setConfirmPassword('');
      setPrimaryEmail('');
      setSecondaryEmail('');
      setShowModal(true);
    } catch (error) {
      if (error.status === 400) {
        if (error.data?.error) {
          setError(error.data.error);
        }
        else if (error.data?.errors) {
          const validationMessages = Object.values(error.data.errors)
            .flat()
            .join(', ');
          setError(validationMessages);
        } else {
          setError('Unexpected error occurred.');
        }
      } else if (error.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Unexpected error occurred.');
      }
    }
  }

  return (
    <>
      <ListGroup className="login-card-body" variant="flush">
        <ListGroup.Item>
          <div className="resetDefaultTitle text-center mt-4">
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#E47B00' }} size='xl' />
            <span style={{ color: '#E47B00', opacity: '0.8', marginLeft: '0.5em' }}> Please set a password for your account.</span>
          </div>
        </ListGroup.Item>
        <Form onSubmit={handleSubmit}>
          <ListGroup.Item>
            <Form.Group id="newPassword">
              <Form.Label>Enter New Password</Form.Label>
              <div>
                <input 
                  type="text" 
                  name="fakeuser" 
                  autoComplete="off" 
                  style={{ display: 'none' }} 
                />
                <input 
                  type="password" 
                  name="fakepass" 
                  autoComplete="off" 
                  style={{ display: 'none' }} 
                />
                <Form.Control
                  className={passwordFocused ? 'loginTextBox NewPasswordFocus' : 'loginTextBox'}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  type={showConfirmPassword ? 'text' : 'password'}
                  onChange={handleNewPasswordChange}
                  value={newPassword}
                  autoComplete="new-password"
                  required
                />
                <FontAwesomeIcon
                  className="loginTextboxIcon"
                  style={{ top: passwordFocused ? '20%' : '' }}
                  icon={faLock}
                />
              </div>
              <div
                className={
                  passwordFocused
                    ? 'newPasswordRequirements showRequirements'
                    : 'newPasswordRequirements'
                }>
                <div>
                  <FontAwesomeIcon
                    style={{ marginRight: '7px' }}
                    icon={passwordValid ? faCircleCheck : faCircleXmark}
                    color={passwordValid ? '#52C9A5' : '#F84646'}
                  />
                  <span>Cannot be the same as your Login ID</span>
                </div>
                <div>
                  <FontAwesomeIcon
                    style={{ marginRight: '7px' }}
                    icon={passwordLengthValid ? faCircleCheck : faCircleXmark}
                    color={passwordLengthValid ? '#52C9A5' : '#F84646'}
                  />
                  <span>Must be at least 4 characters in length</span>
                </div>
                <div>
                  <FontAwesomeIcon
                    style={{ marginRight: '7px' }}
                    icon={passwordCharactersValid ? faCircleCheck : faCircleXmark}
                    color={passwordCharactersValid ? '#52C9A5' : '#F84646'}
                  />
                  <span>{'Cannot include the following special characters (",\'</)'}</span>
                </div>
              </div>
            </Form.Group>
          </ListGroup.Item>

          <ListGroup.Item>
            <Form.Group id="confirmPassword">
              <Form.Label>Confirm New Password</Form.Label>
              <div>
                <Form.Control
                  className="loginTextBox"
                  type={showConfirmPassword ? 'text' : 'password'}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={handleConfirmPasswordBlur}
                  value={confirmPassword}
                  autoComplete="confirm-password"
                  required
                />
                <FontAwesomeIcon
                  className="loginTextboxIcon"
                  style={{ cursor: 'pointer' }}
                  icon={faEye}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              </div>
            </Form.Group>
          </ListGroup.Item>
          {!passwordsMatch && (
            <ListGroup.Item>
              <span style={{ color: '#F84646' }}>Passwords do not match</span>
            </ListGroup.Item>
          )}

          <ListGroup.Item>
            <Form.Group id="userName">
              <Form.Label>
                Primary Email &nbsp;
                <OverlayTrigger
                  key="visibilityStatus"
                  placement="top"
                  overlay={<Tooltip className="custom-tooltip" >
                    <div className='w-100'>
                        Primary Email Id is used as an alternative Login Id. <br/>
                        This also allow Castle Learning to email your ID & Password to you in case you cannot remembet them.
                    </div>
                  </Tooltip>}>
                  <FontAwesomeIcon icon={faCircleQuestion} color="#F78F81" />
                </OverlayTrigger>
              </Form.Label>
              <div>
                <Form.Control
                  className="loginTextBox"
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                />
                <FontAwesomeIcon className="loginTextboxIcon" icon={faEnvelopeDot} />
              </div>
            </Form.Group>
          </ListGroup.Item>

          <ListGroup.Item>
            <Form.Group id="userName">
              <Form.Label>
                Secondary Email (Optional)&nbsp;
                <OverlayTrigger
                  key="visibilityStatus"
                  placement="top"
                  overlay={<Tooltip className="custom-tooltip" >
                    <div className='w-100'>
                       You can provide a secondary email address to be used for password reset puposes.
                    </div>
                  </Tooltip>}>
                  <FontAwesomeIcon icon={faCircleQuestion} color="#F78F81" />
                </OverlayTrigger>   
              </Form.Label>
              <div>
                <Form.Control
                  className="loginTextBox"
                  type="email"
                  value={secondaryEmail}
                  onChange={(e) => setSecondaryEmail(e.target.value)}
                />
                <FontAwesomeIcon className="loginTextboxIcon" icon={faEnvelope} />
              </div>
            </Form.Group>
          </ListGroup.Item>

          <ListGroup.Item>
            <Row className='mt-4'>
              <Col xs={6}>
                <Button
                  disabled={!passwordValid || !passwordLengthValid || !passwordCharactersValid || !passwordsMatch || isLoading}
                  variant="castleRed"
                  className="submitButton"
                  type="submit">
              Update
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  variant="castleWhite"
                  className="backButton"
                  onClick={() => navigate('/login')}>
               Cancel
                </Button>
              </Col>
            </Row>
          </ListGroup.Item>
        </Form>

        {error && (
          <ListGroup.Item>
            <div className="text-center">
              <div className='mx-auto error'>
                {error}
              </div>
            </div>
          </ListGroup.Item>
        )}
      </ListGroup>

      <Modal
        show={showModal}
        centered
        style={{ '--content-width': '500px' }}>
        <div className="d-flex flex-column align-items-center p-3">
          <div
            style={{
              backgroundImage: `url(${successImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              height: '120px',
              width: '100%'
            }}></div>
          <div className="resetTextSuccess m-3 text-color">
            {'Password has been updated successfully. '}
            {'You can now login with your new password'}
          </div>
          <div className='text-center'>
            <Button style={{ padding: '10px 24px' }} variant="castleRed" onClick={() => navigate('/login')}>Ok</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ResetDefaultPassword;
